import React, { useState, Fragment } from 'react';
import {
    Card,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Spinner,
    Table,
    InputGroup
} from 'react-bootstrap';
import {
    FileText,
    Upload,
    Download,
    Copy,
    CheckCircle,
    X,
    MapPin,
    RefreshCw
} from 'lucide-react';

const KMLExtractor = () => {
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [coordinates, setCoordinates] = useState([]);
    const [coordinateGroups, setCoordinateGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [copiedGroupId, setCopiedGroupId] = useState(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.name.endsWith('.kml') || selectedFile.name.endsWith('.KML')) {
                setFile(selectedFile);
                setFileName(selectedFile.name);
                setError('');
                setCoordinates([]);
            } else {
                setError('Please select a valid .kml file');
                setFile(null);
                setFileName('');
            }
        }
    };

    const parseKML = (xmlText) => {
        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
            
            // Check for parsing errors
            const parserError = xmlDoc.querySelector('parsererror');
            if (parserError) {
                throw new Error('Invalid XML format');
            }

            // Find all coordinates tags
            const coordinatesElements = xmlDoc.getElementsByTagName('coordinates');
            
            if (coordinatesElements.length === 0) {
                throw new Error('No <coordinates> tags found in the KML file');
            }

            const extractedGroups = [];
            
            // Process each coordinates element
            Array.from(coordinatesElements).forEach((coordElement, groupIndex) => {
                const coordText = coordElement.textContent.trim();
                
                // Split by whitespace (coordinates can be space or newline separated)
                const coordPairs = coordText.split(/\s+/).filter(coord => coord.trim() !== '');
                
                const groupCoordinates = [];
                
                coordPairs.forEach((coordPair, pairIndex) => {
                    // Split by comma: longitude,latitude,altitude
                    const parts = coordPair.split(',');
                    
                    if (parts.length >= 2) {
                        const longitude = parseFloat(parts[0].trim());
                        const latitude = parseFloat(parts[1].trim());
                        
                        // Validate coordinates
                        if (!isNaN(longitude) && !isNaN(latitude)) {
                            // Swap: store as latitude, longitude (removed altitude)
                            groupCoordinates.push({
                                id: `${groupIndex}-${pairIndex}`,
                                latitude: latitude,
                                longitude: longitude,
                                original: coordPair
                            });
                        }
                    }
                });
                
                if (groupCoordinates.length > 0) {
                    extractedGroups.push({
                        groupId: groupIndex,
                        groupNumber: groupIndex + 1,
                        coordinates: groupCoordinates
                    });
                }
            });

            if (extractedGroups.length === 0) {
                throw new Error('No valid coordinates found in the KML file');
            }

            return extractedGroups;
        } catch (err) {
            throw new Error(`Error parsing KML file: ${err.message}`);
        }
    };

    const handleProcess = async () => {
        if (!file) {
            setError('Please select a KML file first');
            return;
        }

        setLoading(true);
        setError('');
        setCoordinates([]);

        try {
            const fileText = await file.text();
            const extracted = parseKML(fileText);
            setCoordinateGroups(extracted);
            // Flatten all coordinates for copy/download functionality
            const allCoordinates = extracted.flatMap(group => group.coordinates);
            setCoordinates(allCoordinates);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setFileName('');
        setCoordinates([]);
        setCoordinateGroups([]);
        setError('');
        setCopied(false);
        setCopiedGroupId(null);
        // Reset file input
        const fileInput = document.getElementById('kml-file-input');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const getCoordinatesString = () => {
        return coordinates.map(coord => `${coord.latitude},${coord.longitude}`).join(' ');
    };

    const writeClipboard = (text) => {
        if (navigator.clipboard && window.isSecureContext) {
            return navigator.clipboard.writeText(text);
        }
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return Promise.resolve();
    };

    const copyToClipboard = () => {
        const coordsString = getCoordinatesString();
        writeClipboard(coordsString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const copyGroupToClipboard = (groupCoordinates) => {
        const coordsString = groupCoordinates.map(coord => `${coord.latitude},${coord.longitude}`).join(' ');
        writeClipboard(coordsString);
        setCopiedGroupId(groupCoordinates[0]?.id?.split('-')[0] || null);
        setTimeout(() => setCopiedGroupId(null), 2000);
    };

    const downloadCoordinates = () => {
        const coordsString = getCoordinatesString();
        const blob = new Blob([coordsString], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName.replace('.kml', '_all_coordinates.txt').replace('.KML', '_all_coordinates.txt');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadGroupCoordinates = (groupCoordinates, groupNumber) => {
        const coordsString = groupCoordinates.map(coord => `${coord.latitude},${coord.longitude}`).join(' ');
        const blob = new Blob([coordsString], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName.replace('.kml', `_group${groupNumber}_coordinates.txt`).replace('.KML', `_group${groupNumber}_coordinates.txt`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-primary fw-bold">
                                    <MapPin size={32} className="me-3" />
                                    KML Coordinate Extractor
                                </h1>
                                <p className="text-muted mb-0">
                                    Extract and process coordinates from Google Earth KML files
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Alert */}
                {error && (
                    <Row className="mb-4">
                        <Col md={12}>
                            <Alert variant="danger" dismissible onClose={() => setError('')}>
                                <X size={16} className="me-2" />
                                {error}
                            </Alert>
                        </Col>
                    </Row>
                )}

                {/* Upload Section */}
                <Row className="mb-4">
                    <Col md={8} lg={6}>
                        <Card className="shadow-sm border-0">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Upload size={18} className="me-2" />
                                    Upload KML File
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        <FileText size={16} className="me-2 text-info" />
                                        Select KML File
                                    </Form.Label>
                                    <Form.Control
                                        id="kml-file-input"
                                        type="file"
                                        accept=".kml,.KML"
                                        onChange={handleFileChange}
                                        disabled={loading}
                                    />
                                    <Form.Text className="text-muted">
                                        Upload a .kml file exported from Google Earth
                                    </Form.Text>
                                </Form.Group>

                                {fileName && (
                                    <div className="mb-3">
                                        <Alert variant="info" className="mb-0">
                                            <FileText size={16} className="me-2" />
                                            Selected: <strong>{fileName}</strong>
                                        </Alert>
                                    </div>
                                )}

                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={handleProcess}
                                        disabled={!file || loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <MapPin size={16} className="me-2" />
                                                Extract Coordinates
                                            </>
                                        )}
                                    </Button>
                                    {(file || coordinates.length > 0) && (
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleReset}
                                            disabled={loading}
                                        >
                                            <RefreshCw size={16} className="me-2" />
                                            Reset
                                        </Button>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Results Section */}
                {coordinateGroups.length > 0 && (
                    <Row>
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <CheckCircle size={18} className="me-2 text-success" />
                                    Extracted Coordinates ({coordinates.length} total from {coordinateGroups.length} group{coordinateGroups.length !== 1 ? 's' : ''})
                                </h5>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={copyToClipboard}
                                    >
                                        {copied ? (
                                            <>
                                                <CheckCircle size={14} className="me-2" />
                                                Copied!
                                            </>
                                        ) : (
                                            <>
                                                <Copy size={14} className="me-2" />
                                                Copy All
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={downloadCoordinates}
                                    >
                                        <Download size={14} className="me-2" />
                                        Download All
                                    </Button>
                                </div>
                            </div>

                            {/* Multiple Tables - One per coordinate group */}
                            {coordinateGroups.map((group, groupIndex) => (
                                <Card key={group.groupId} className="shadow-sm border-0 mb-4">
                                    <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0 d-flex align-items-center">
                                            <MapPin size={16} className="me-2" />
                                            Coordinates Group {group.groupNumber} ({group.coordinates.length} coordinates)
                                        </h6>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="light"
                                                size="sm"
                                                onClick={() => copyGroupToClipboard(group.coordinates)}
                                            >
                                                {copiedGroupId === String(group.groupId) ? (
                                                    <>
                                                        <CheckCircle size={14} className="me-2" />
                                                        Copied!
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy size={14} className="me-2" />
                                                        Copy Group
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                variant="light"
                                                size="sm"
                                                onClick={() => downloadGroupCoordinates(group.coordinates, group.groupNumber)}
                                            >
                                                <Download size={14} className="me-2" />
                                                Download Group
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <div className="table-responsive">
                                            <Table striped hover responsive className="mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Latitude</th>
                                                        <th>Longitude</th>
                                                        <th>Formatted (Lat, Long)</th>
                                                        <th>Original</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {group.coordinates.map((coord, index) => (
                                                        <tr key={coord.id}>
                                                            <td>{index + 1}</td>
                                                            <td className="fw-bold text-primary">
                                                                {coord.latitude.toFixed(6)}
                                                            </td>
                                                            <td className="fw-bold text-info">
                                                                {coord.longitude.toFixed(6)}
                                                            </td>
                                                            <td>
                                                                <code className="bg-light px-2 py-1 rounded">
                                                                    {coord.latitude.toFixed(6)},{coord.longitude.toFixed(6)}
                                                                </code>
                                                            </td>
                                                            <td className="text-muted small">
                                                                <code>{coord.original}</code>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </Col>
                    </Row>
                )}

                {/* Instructions */}
                {coordinates.length === 0 && !loading && (
                    <Row className="mt-4">
                        <Col md={8} lg={6}>
                            <Card className="shadow-sm border-0 bg-light">
                                <Card.Body>
                                    <h6 className="fw-bold mb-3">How to use:</h6>
                                    <ol>
                                        <li className="mb-2">Export a placemark or path from Google Earth as a .kml file</li>
                                        <li className="mb-2">Click "Choose File" and select your .kml file</li>
                                        <li className="mb-2">Click "Extract Coordinates" to process the file</li>
                                        <li className="mb-2">The tool will extract all coordinates from &lt;coordinates&gt; tags</li>
                                        <li className="mb-2">Coordinates are processed: altitude removed, lat/long swapped</li>
                                        <li>Copy or download the processed coordinates</li>
                                    </ol>
                                    <div className="mt-3">
                                        <strong>Note:</strong> The tool extracts coordinates in the format <code>latitude,longitude</code> (altitude removed, order swapped from original KML format).
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        </Fragment>
    );
};

export default KMLExtractor;

